<template>
<v-container fluid fill-height>
    <v-layout v-if="loading" align-center justify-center>
        <v-progress-circular indeterminate></v-progress-circular>
    </v-layout>
    <v-layout v-else column>
        <v-flex v-if="error != null" xs12>
            <v-alert :value="error != null" outline type="error">
                {{error}}
            </v-alert>
        </v-flex>
        <v-card v-else>
            <v-toolbar color="primary" class="white--text">
                <v-toolbar-title>Crop<span v-if="clone"> - Clone</span></v-toolbar-title>
                <v-spacer></v-spacer>
                <v-icon large dark>grain</v-icon>
            </v-toolbar>
            <v-stepper v-model="step">
                <v-stepper-header>
                    <v-stepper-step :complete="step > 1" step="1">Crop Details</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step :complete="step > 2" step="2">Crop Rows</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step :complete="step > 3" step="3">Mandatory Variety Attributes</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step step="4">Optional Variety Attributes</v-stepper-step>
                </v-stepper-header>
                <v-stepper-items>
                    <v-stepper-content step="1">
                        <crop-details v-if="step == 1" :crop="crop" :clone="clone" :submitError="submitError" :submitLoading="submitLoading" @next="handleCropDetailsNext" @cancel="handleCancel" @updateToDate="updateAllAttributesToDate"></crop-details>
                    </v-stepper-content>
                    <v-stepper-content step="2">
                        <crop-rows v-if="step == 2" :crop="crop" :submitError="submitError" :submitLoading="submitLoading" @next="handleCropRowsNext" @cancel="handleCancel" @previous="step = 1"></crop-rows>
                    </v-stepper-content>
                    <v-stepper-content step="3">
                        <crop-attributes-mandatory v-if="step == 3" :crop="crop" :submitError="submitError" :submitLoading="submitLoading" @next="handleCropMandatoryAttributesNext" @cancel="handleCancel" @previous="step = 2"></crop-attributes-mandatory>
                    </v-stepper-content>
                    <v-stepper-content step="4">
                        <crop-attributes-optional v-if="step == 4" :crop="crop" :submitError="submitError" :submitLoading="submitLoading" @next="handleCropOptionalAttributesNext" @cancel="handleCancel" @previous="step = 3"></crop-attributes-optional>
                    </v-stepper-content>
                </v-stepper-items>
            </v-stepper>
        </v-card>
    </v-layout>
</v-container>
</template>

<script>
import CropDetails from '@/components/crops/CropDetails.vue';
import CropRows from '@/components/crops/CropRows.vue';
import CropAttributesMandatory from '@/components/crops/CropAttributesMandatory.vue';
import CropAttributesOptional from '@/components/crops/CropAttributesOptional.vue';
import moment from 'moment'
import cropApi from '@/api/cropApi';

import { mapState } from 'vuex';

export default {
    components: {
        CropDetails: CropDetails,
        CropRows:CropRows,
        CropAttributesMandatory: CropAttributesMandatory,
        CropAttributesOptional: CropAttributesOptional
    },
    data() {
        return {
            loading: false,
            error: null,
            submitError: null,
            submitLoading: false,
            clone:false,
            step: 0,
            crop: null,
            locations: [],
            productTypes: [],
            key:0,
            financialYears: []
        }
    },
    computed: {
        ...mapState("CropStore", [
            "defaultCropLength",
        ]),
    },
    methods: {
        async load(plantID, cropID, clone) {
            this.loading = true;
            this.clone = clone == 'True';            

            try {
                await this.$store.dispatch("CropStore/loadLookups", plantID);

                if (!cropID) {
                    this.setupNewCrop();
                } else {
                    this.crop = await cropApi.getCrop(cropID, clone);
                }

            } catch (error) {
                this.error = error;
            }

            this.loading = false;
        },
        async setupNewCrop() {
            this.crop = {
                PlantID: this.$route.query.plantID,
                StartDate: new moment().format('YYYY-MM-DD'),
                EndDate: null,
                FinancialYear: new moment().year().toString(),
                CropRows: [],
                CropVarieties: []
            };            

            if (this.defaultCropLength > 0) {
                this.crop.EndDate = new moment(this.crop.StartDate).add(this.defaultCropLength, 'days').format('YYYY-MM-DD');
            } else {
                this.crop.EndDate = new moment(this.crop.StartDate).add(7, 'days').format('YYYY-MM-DD');
            }
        },
        async handleCropDetailsNext() {
            this.submitLoading = true;
            this.submitError = null;

            try {
                let cropDetail = this.mapCropDetail(this.crop);
                let result = await cropApi.isValid(cropDetail);
                
                if (!result.Success) {
                    this.submitError = result.ErrorMessages.join(' ');
                } else {
                    // Default Start and End Dates for attributes when cloning.
                    if (this.clone) {
                        for (const cropVariety of this.crop.CropVarieties) {
                            for (const attribute of cropVariety.Attributes) {
                                for (const value of attribute.Values) {
                                    if (!value.StartDate) {
                                        value.StartDate = this.crop.PlantingDate || this.crop.StartDate;
                                        value.EndDate = this.crop.EndDate;
                                    }
                                }
                            }
                        }
                    }

                    this.step = 2;
                }

            } catch (error) {
                this.submitError = error;
            }

            this.submitLoading = false;
        },
        async handleCropRowsNext() {
            this.step = 3;
        },
        async handleCropMandatoryAttributesNext() {
            this.submitLoading = true;
            this.submitError = null;

            try {
                for (const element of this.crop.CropVarieties) {
                    let cropDetail = this.mapCropDetail(this.crop);
                    cropDetail.CropVarieties = [ {
                            PlantingDate: element.PlantingDate,
                            Attributes: element.Attributes.filter(a => a.AttributeMandatory)
                        } ];                    
                    
                    let result = await cropApi.isValid(cropDetail);

                    if (!result.Success) {
                        this.submitError = "Check Validation errors for each Variety";
                        element.ErrorMessages = result.ErrorMessages;
                    } else {
                        element.ErrorMessages = null;
                    }
                }

                if (!this.submitError) {
                    this.step = 4;
                }                

            } catch (error) {
                this.submitError = error;
            }

            this.submitLoading = false;
        },
        async handleCropOptionalAttributesNext() {
            this.submitLoading = true;
            this.submitError = null;

            try {
                for (const element of this.crop.CropVarieties) {
                    let cropDetail = this.mapCropDetail(this.crop);
                    cropDetail.CropVarieties= [ {
                            PlantingDate: element.PlantingDate,
                            Attributes: element.Attributes.filter(a => !a.AttributeMandatory)
                        } ];                    
                    
                    let result = await cropApi.isValid(cropDetail);

                    if (!result.Success) {
                        this.submitError = "Check Validation errors for each Variety";
                        element.ErrorMessages = result.ErrorMessages;
                    } else {
                        element.ErrorMessages = null;
                    }
                }

                if (!this.submitError) {
                    // Save and finish
                    let result = await cropApi.save(this.crop);

                    if (!result.Success) {
                        this.submitError = result.ErrorMessages.join(" ");                        
                    } else {
                        this.handleCancel();
                    }
                }                

            } catch (error) {
                this.submitError = error;
            }

            this.submitLoading = false;
        },
        async handleCancel() {
            try {
                parent.CloseRadWindow();
            } catch (error) {
                this.error = error;
            }
        },
        mapCropDetail(crop) {
            return {
                CropID: crop.CropID,
                ProductTypeID: crop.ProductTypeID,
                LocationID: crop.LocationID,
                PlantingDate: crop.PlantingDate,
                StartDate: crop.StartDate,
                EndDate: crop.EndDate
            }
        },
        updateAllAttributesToDate(){
            this.crop.endDateChanged = true;
            console.log(this.crop.endDateChanged );
            this.crop.CropVarieties.forEach(variety => {
                variety.Attributes.forEach(attribute => {
                    let max=0;
                    attribute.Values.forEach((v, k)=>{
                        if(max == 0){
                            max=v.StartDate;
                            this.key = k;
                        }
                        if (max < v.StartDate) { 
                            max = v.StartDate; 
                            this.key = k; 
                        }
                    });
                    attribute.Values[this.key].EndDate = this.crop.EndDate;
                    
                })
            });
        }
    },
    async created() {
        this.load(this.$route.query.plantID, this.$route.params.cropID, this.$route.query.clone);
    }
}
</script>
